import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  useAttachExperienceOfferUrgencyTag,
  useDetachExperienceOfferUrgencyTag,
  useGetExperienceUrgencyTags,
} from '~/hooks/apis/experiences';

import { ExperienceUrgencyTag } from '~/services/ExperiencesService';

import getErrorMessage from '~/utils/getErrorMessage';

const EMPTY_URGENCY_TAGS: Array<ExperienceUrgencyTag> = [];

interface Props {
  offerId: string;
  urgencyTags?: Array<ExperienceUrgencyTag>;
  disabled: boolean;
}

function TagPreview({ tag }: { tag: ExperienceUrgencyTag }) {
  return (
    <Box
      style={{
        backgroundColor: tag.bgColor,
        color: tag.textColor,
      }}
      sx={{
        display: 'inline-flex',
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        typography: 'caption',
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {tag.text}
    </Box>
  );
}

export default function ExperienceUrgencyTags({ offerId, urgencyTags, disabled }: Props) {
  const sourceTag = urgencyTags?.[0] ?? null;
  const [tag, setTag] = useState<ExperienceUrgencyTag | null>(() => sourceTag);
  const [selectedTagId, setSelectedTagId] = useState(() => sourceTag?.id ?? '');
  const [tagPendingDetach, setTagPendingDetach] = useState<ExperienceUrgencyTag | null>(null);
  const [saveSucceeded, setSaveSucceeded] = useState(false);
  const catalogueQuery = useGetExperienceUrgencyTags();
  const attachUrgencyTag = useAttachExperienceOfferUrgencyTag();
  const detachUrgencyTag = useDetachExperienceOfferUrgencyTag();

  useEffect(() => {
    setTag(sourceTag);
    setSelectedTagId(sourceTag?.id ?? '');
    setSaveSucceeded(false);
  }, [offerId, sourceTag]);

  const mutationError = attachUrgencyTag.error ?? detachUrgencyTag.error;
  const error = mutationError ?? catalogueQuery.error;
  const isPending = attachUrgencyTag.isPending || detachUrgencyTag.isPending;

  const attachTag = async () => {
    if (!selectedTagId) return;

    try {
      const savedTag = await attachUrgencyTag.mutateAsync({ offerId, urgencyTagId: selectedTagId });
      setTag(savedTag);
      setSelectedTagId(savedTag.id);
      setSaveSucceeded(true);
    } catch {
      // The mutation error is shown in the alert above the form.
    }
  };

  const confirmDetachTag = async () => {
    if (!tag) return;

    try {
      await detachUrgencyTag.mutateAsync({ offerId, urgencyTagId: tag.id });
      setTag(null);
      setSelectedTagId('');
      setTagPendingDetach(null);
      setSaveSucceeded(true);
    } catch {
      // The mutation error is shown in the alert above the form.
    }
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
        Urgency tag
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Displayed on experience images during checkout only.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error)}
        </Alert>
      )}
      {saveSucceeded && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Urgency tag saved
        </Alert>
      )}

      {tag && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TagPreview tag={tag} />
          <IconButton
            aria-label={`Detach urgency tag ${tag.text}`}
            onClick={() => setTagPendingDetach(tag)}
            disabled={disabled || isPending}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      )}

      {!tag && (
        <Typography variant="body2" color="text.secondary">
          No urgency tag attached
        </Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }} alignItems={{ sm: 'center' }}>
        <FormControl
          size="small"
          sx={{ width: { xs: '100%', sm: 300 } }}
          disabled={
            disabled ||
            isPending ||
            !!tag ||
            catalogueQuery.isLoading ||
            !(catalogueQuery.data ?? EMPTY_URGENCY_TAGS).length
          }
        >
          <InputLabel id="urgency-tag-select-label">Urgency tag</InputLabel>
          <Select
            labelId="urgency-tag-select-label"
            label="Urgency tag"
            value={selectedTagId}
            onChange={(event) => setSelectedTagId(event.target.value)}
          >
            {(catalogueQuery.data ?? EMPTY_URGENCY_TAGS).map((catalogueTag) => (
              <MenuItem key={catalogueTag.id} value={catalogueTag.id}>
                <Tooltip title={<TagPreview tag={catalogueTag} />} placement="right" arrow>
                  <Box component="span" sx={{ display: 'block', width: '100%' }}>
                    {catalogueTag.text}
                  </Box>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={catalogueQuery.isLoading ? <CircularProgress size={16} /> : <AddIcon />}
          onClick={() => void attachTag()}
          disabled={disabled || isPending || !!tag || !selectedTagId}
          sx={{ minWidth: { sm: 140 }, whiteSpace: 'nowrap' }}
        >
          {attachUrgencyTag.isPending ? 'Attaching…' : 'Attach tag'}
        </Button>
      </Stack>

      <Dialog open={!!tagPendingDetach} onClose={() => setTagPendingDetach(null)}>
        <DialogTitle>Detach urgency tag?</DialogTitle>
        <DialogContent>
          <Typography>Remove “{tagPendingDetach?.text || 'this urgency tag'}” from this experience?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagPendingDetach(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void confirmDetachTag()} disabled={isPending}>
            {detachUrgencyTag.isPending ? 'Detaching…' : 'Detach'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
